import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const PageEditor = () => {
  const [loading, setLoading] = useState(false);
  const agent = JSON.parse(localStorage.getItem('agent') || '{}');
  const [content, setContent] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_cta_primary: 'Recevoir mon estimation',
    hero_cta_secondary: 'Parler à un expert',
    section_expertise: '',
    section_methodology: ''
  });

  useEffect(() => {
    // Load existing content
    const loadContent = async () => {
      try {
        const data = await api.getAgentPublic(agent.slug);
        if (data.content && Object.keys(data.content).length > 0) {
          setContent(prev => ({ ...prev, ...data.content }));
        }
      } catch (error) {
        console.error('Failed to load content:', error);
      }
    };
    if (agent.slug) loadContent();
  }, [agent.slug]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.updatePageContent(agent.slug, content);
      toast.success('Contenu mis à jour', {
        description: 'Les modifications sont visibles sur votre page.'
      });
    } catch (error) {
      toast.error('Erreur', {
        description: 'Impossible de sauvegarder les modifications.'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold mb-2">Éditeur de page</h1>
        <p className="text-muted-foreground">
          Personnalisez le contenu de votre landing page<br />
          <span className="text-sm">Votre page: <code className="bg-muted px-2 py-1 rounded">{window.location.origin}/a/{agent.slug}</code></span>
        </p>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList>
          <TabsTrigger value="hero">Hero</TabsTrigger>
          <TabsTrigger value="sections">Sections</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <Label htmlFor="hero_title">Titre principal</Label>
              <Input
                id="hero_title"
                value={content.hero_title}
                onChange={(e) => updateField('hero_title', e.target.value)}
                placeholder={`Estimation Immobilière à ${agent.city}`}
                data-testid="hero-title-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si vide, le titre par défaut sera utilisé
              </p>
            </div>

            <div>
              <Label htmlFor="hero_subtitle">Sous-titre</Label>
              <Textarea
                id="hero_subtitle"
                value={content.hero_subtitle}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                rows={3}
                placeholder="Votre expert local pour une évaluation précise..."
                data-testid="hero-subtitle-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cta_primary">CTA principal</Label>
                <Input
                  id="cta_primary"
                  value={content.hero_cta_primary}
                  onChange={(e) => updateField('hero_cta_primary', e.target.value)}
                  data-testid="cta-primary-input"
                />
              </div>
              <div>
                <Label htmlFor="cta_secondary">CTA secondaire</Label>
                <Input
                  id="cta_secondary"
                  value={content.hero_cta_secondary}
                  onChange={(e) => updateField('hero_cta_secondary', e.target.value)}
                  data-testid="cta-secondary-input"
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sections" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <Label htmlFor="section_expertise">Section Expertise Locale</Label>
              <Textarea
                id="section_expertise"
                value={content.section_expertise}
                onChange={(e) => updateField('section_expertise', e.target.value)}
                rows={4}
                placeholder="Parlez de votre connaissance du marché local, des quartiers, etc."
                data-testid="expertise-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Facultatif - Si vide, le texte par défaut sera affiché
              </p>
            </div>

            <div>
              <Label htmlFor="section_methodology">Section Méthodologie</Label>
              <Textarea
                id="section_methodology"
                value={content.section_methodology}
                onChange={(e) => updateField('section_methodology', e.target.value)}
                rows={4}
                placeholder="Expliquez votre méthode d'estimation, vos outils..."
                data-testid="methodology-input"
              />
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex gap-4">
        <Button onClick={handleSave} disabled={loading} data-testid="save-page-content">
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
        <Button variant="outline" onClick={() => window.open(`/a/${agent.slug}`, '_blank')}>
          Prévisualiser
        </Button>
      </div>
    </div>
  );
};

export default PageEditor;
